import { Router, watchBusy } from "@frui.ts/screens/dist";
import EventBus, { lustrationReportFinish, lustrationReportProcessed } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import { Modules } from "config";
import ConfirmationService from "../../../services/confirmationService";
import ListViewModel from "../../listViewModel";
import LustrationReportsCreateViewModel from "viewModels/enforcement/lustrationReports/lustrationReportsCreateViewModel";
import LustrationReportResponse from "entities/lustrationReportResponse";
import LustrationRepository from "repositories/lustrationRepository";
import { action, computed, observable } from "mobx";
import EnumService from "services/enumService";
import { attachAutomaticValidator, hideValidationErrors } from "@frui.ts/validation";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import DateTimeOffsetRangeRequest from "entities/dateTimeOffsetRangeRequest";
import LustrationReportsSearchRequest, { LustrationReportSearchValidationRules } from "entities/lustrationReportsSearchRequest";
import * as Sentry from "@sentry/react";
import download from "downloadjs";
import { formatLicensePlate } from "utils/localizations";
import LustrationReportsService from "services/lustrationReportsService";
const navigationName = "lustration";

@Router.registerRoute({
  route: navigationName,
  name: Router.Self,
  children: [LustrationReportsCreateViewModel],
})
export default class LustrationReportsViewModel extends ListViewModel<
  LustrationReportResponse,
  LustrationReportsCreateViewModel
> {
  readonly modelName = "lustrationReport";
  protected abortController?: AbortController;
  @observable fileResponse: any = undefined;
  countrySelectViewModel: CountrySelectViewModel;
  createViewModel: LustrationReportsCreateViewModel;

  constructor(
    protected createViewModelFactory: ReturnType<typeof LustrationReportsCreateViewModel.Factory>,
    protected repository: LustrationRepository,
    protected localizationService: LocalizationService,
    public notificationService: NotificationService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected confirmationService: ConfirmationService,
    protected enumService: EnumService,
    private lustrationReportsService: LustrationReportsService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.navigationName = navigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
    this.createViewModel = this.createViewModelFactory();
  }

  onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      this.eventBus.subscribe(lustrationReportFinish, this.onReportFinished),
      this.eventBus.subscribe(lustrationReportProcessed, this.onReportProcessed)
    );
    return super.onInitialize();
  }

  protected resetFilterValues(filter: LustrationReportsSearchRequest) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
  }

  protected createFilter(): LustrationReportsSearchRequest {
    return attachAutomaticValidator(new LustrationReportsSearchRequest(), LustrationReportSearchValidationRules);
  }

  @computed
  get allowToDownload() {
    if (this.allowRead === false) {
      return false;
    }
    return true;
  }

  @action.bound
  onAddClick() {
    const createViewModelFactory = this.createViewModelFactory;
    if (createViewModelFactory && this.allowWrite) {
      return () => this.tryActivateChild(createViewModelFactory());
    } else {
      return undefined;
    }
  }

  @action.bound
  @watchBusy
  public async loadData() {
    if (this.filter._eventOccurredAtSinceDate || this.filter._eventOccurredAtUntilDate) {
      this.filter.eventOccurredAtRange = new DateTimeOffsetRangeRequest();
    }
    if (this.filter._eventOccurredAtSinceDate && this.filter.eventOccurredAtRange) {
      this.filter.eventOccurredAtRange.from = new Date(this.filter._eventOccurredAtSinceDate);
    }
    if (this.filter._eventOccurredAtUntilDate && this.filter.eventOccurredAtRange) {
      this.filter.eventOccurredAtRange.to = new Date(this.filter._eventOccurredAtUntilDate);
    }

    this.abortController = new AbortController();

    const result = await this.repository.getAll(this.filter, this.pagingFilter, this.abortController?.signal);
    this.abortController = undefined;

    const items = result.items.map(item => this.enumService.proxyObjectEnums(item));
    this.setData([
      items,
      {
        limit: result.metadata.limit,
        offset: result.metadata.offset,
        totalItems: result.metadata.total,
      },
    ]);
  }

  @action.bound
  @watchBusy
  async onDownload(item: LustrationReportResponse) {
    await this.download(item);
  }

  async download(item: LustrationReportResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        const fileNameLicensePlate = formatLicensePlate(item.vehicleRegistration).replace(" – ", "-");
        const fileNameSequence = item.reportSequence.replace("/", "-");
        download(
          this.fileResponse.data,
          `${this.translate("downloadFileName")
            .replace("%LICENSE_PLATE%", fileNameLicensePlate)
            .replace("%SEQUENCE%", fileNameSequence)}.pdf`,
          "application/pdf"
        );
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  @action.bound
  async onReportFinished(event: ReturnType<typeof lustrationReportFinish>) {
    const { confirmed, generate } = await this.lustrationReportsService.showFinishedConfirmation();
    const report = this.items.find(x => x.id === event.payload.lustrationReport.existingReportId);
    if ((!confirmed && !generate) || !event.payload.lustrationReport.existingReportId) {
      return;
    }

    if (confirmed && !generate && report) {
      try {
        await this.download(report);
        this.notificationService.add({ title: this.translate("exported"), type: NotificationType.SUCCESS });
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    if (confirmed && generate) {
      try {
        if (report) {
          await this.createViewModel.reGenerate(report);
          await this.loadData();
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }

  @action.bound
  async onReportProcessed(event: ReturnType<typeof lustrationReportProcessed>) {
    const confirmed = await this.lustrationReportsService.showProcessedConfirmation();
    if (confirmed) {
      return;
    }
  }

  allowItemDetail(item: LustrationReportResponse): boolean {
    return false;
  }

  allowItemDelete(item: LustrationReportResponse): boolean {
    return false;
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.enforcement, `lustrationReports.${code}`, size);
  }

  translateFilterAttribute(code: string, size?: number): string {
    return this.localizationService.translateAttribute("lustrationReports", code, size);
  }

  translateStatus(code: string): string {
    return this.localizationService.translateModule(Modules.enforcement, `lustrationReports.statusTypes.${code}`);
  }
}
